import { useEffect, useMemo, useState } from "react"
import { motion } from "motion/react"
import { X } from "lucide-react"
import { Copy01, LinkExternal01, RefreshCw02, Plus } from "@untitledui/icons"
import { toast } from "sonner"
import { cx } from "@/lib/utils/cx"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ApiError } from "@/lib/api/client"
import {
  onboardingLinksApi,
  formatMoney,
  type OnboardingLinkSummary,
  type OnboardingStatus,
  type CciStatus,
} from "@/lib/api/onboarding-activation"

// Sales console for activation links — Sam's deal builder. He prices each deal
// by hand as line items (one invoice to the body shop), sets the term and
// free-first-month, prefills what he knows from the call, and sends one link.
// This page is also the field spec for the real admin "generate activation
// link" page. Authenticated. MOCK-ONLY.
export default function OnboardingLinksPage() {
  const [sessions, setSessions] = useState<OnboardingLinkSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)

  const refresh = () =>
    onboardingLinksApi.list().then((r) => setSessions(r.sessions)).catch(() => {})

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [])

  return (
    <ProtectedRoute>
      <AppLayout>
        <div className="mx-auto max-w-5xl px-6 py-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-zinc-900">
                Customer onboarding
              </h1>
              <p className="mt-1 text-[14px] text-zinc-500">
                Price the deal, generate a secure activation link, track it to a booked
                kickoff call. One link per shop, one invoice per shop.
              </p>
            </div>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="flex shrink-0 items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-zinc-800"
            >
              <Plus className="size-4" />
              New link
            </button>
          </div>

          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-6 overflow-hidden"
            >
              <GenerateForm
                onGenerated={() => {
                  refresh()
                }}
              />
            </motion.div>
          )}

          <div className="mt-8">
            {loading ? (
              <div className="grid place-items-center py-16">
                <span className="size-6 animate-spin rounded-full border-2 border-zinc-200 border-t-emerald-600" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-zinc-200 py-16 text-center text-[14px] text-zinc-400">
                No activation links yet. Generate one to get started.
              </div>
            ) : (
              <div className="overflow-hidden rounded-3xl border border-zinc-200">
                <table className="w-full text-left text-[13px]">
                  <thead className="bg-zinc-50 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                    <tr>
                      <th className="px-5 py-3">Body shop</th>
                      <th className="px-5 py-3">Deal</th>
                      <th className="px-5 py-3">Monthly</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {sessions.map((s) => (
                      <SessionRow key={s.id} s={s} onResend={refresh} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}

/* ── Deal builder form ───────────────────────────────────── */

interface DraftItem {
  description: string
  amount: string // dollars, as typed
}

const CCI_OPTIONS: { value: CciStatus; label: string }[] = [
  { value: "unknown", label: "Unknown — default to new-shop path" },
  { value: "new", label: "New shop — needs install" },
  { value: "existing", label: "Already connected (OEM/network)" },
]

function GenerateForm({ onGenerated }: { onGenerated: () => void }) {
  // shop
  const [shopName, setShopName] = useState("")
  const [legalBusinessName, setLegalName] = useState("")
  const [contactEmail, setContactEmail] = useState("")
  const [signerName, setSignerName] = useState("")
  // pricing
  const [items, setItems] = useState<DraftItem[]>([
    { description: "eTX ImpaXt platform", amount: "" },
  ])
  const [term, setTerm] = useState("3")
  // The customer fills their own intake — no address prefill from sales.
  const [cciStatus, setCci] = useState<CciStatus>("unknown")
  // rep
  const [salesRepName, setRepName] = useState("")
  const [salesRepEmail, setRepEmail] = useState("")
  const [salesRepPhone, setRepPhone] = useState("")

  const [submitting, setSubmitting] = useState(false)
  const [generated, setGenerated] = useState<string | null>(null)

  const link = generated ? `${window.location.origin}${generated}` : null

  const totalCents = useMemo(
    () =>
      items.reduce((acc, it) => {
        const v = Math.round(parseFloat(it.amount || "0") * 100)
        return acc + (Number.isFinite(v) && v > 0 ? v : 0)
      }, 0),
    [items],
  )

  const setItem = (i: number, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const lineItems = items
      .map((it) => ({
        description: it.description.trim(),
        monthlyAmountCents: Math.round(parseFloat(it.amount || "0") * 100),
      }))
      .filter((li) => li.description && li.monthlyAmountCents > 0)
    if (!lineItems.length) {
      toast.error("Add at least one line item with an amount")
      return
    }
    setSubmitting(true)
    try {
      const res = await onboardingLinksApi.generate({
        shopName,
        legalBusinessName: legalBusinessName || undefined,
        contactEmail,
        signerName: signerName || undefined,
        lineItems,
        contractTermMonths: Math.max(1, parseInt(term, 10) || 3),
        cciStatus,
        salesRepName,
        salesRepEmail,
        salesRepPhone: salesRepPhone || undefined,
      })
      setGenerated(res.activationUrl)
      toast.success("Activation link generated")
      onGenerated()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn’t generate link")
    } finally {
      setSubmitting(false)
    }
  }

  if (link) {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50/50 p-6">
        <p className="text-[13px] font-semibold text-emerald-700">
          Link ready — this is what the customer receives.
        </p>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-3">
          <span className="flex-1 truncate font-mono text-[13px] text-zinc-700">{link}</span>
          <button
            onClick={() => {
              navigator.clipboard.writeText(link)
              toast.success("Copied")
            }}
            className="flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-[12px] font-semibold text-white"
          >
            <Copy01 className="size-3.5" />
            Copy
          </button>
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-1.5 text-[12px] font-semibold text-zinc-700 hover:bg-zinc-50"
          >
            <LinkExternal01 className="size-3.5" />
            Open
          </a>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-zinc-200 bg-white p-6">
      {/* Shop */}
      <SectionLabel>Body shop</SectionLabel>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <Field label="Shop name *">
          <input value={shopName} onChange={(e) => setShopName(e.target.value)} required className={inputClass} placeholder="Sunrise Collision Center" />
        </Field>
        <Field label="Legal business name">
          <input value={legalBusinessName} onChange={(e) => setLegalName(e.target.value)} className={inputClass} placeholder="Defaults to shop name" />
        </Field>
        <Field label="Signer / primary contact">
          <input value={signerName} onChange={(e) => setSignerName(e.target.value)} className={inputClass} placeholder="Dana Rodriguez" />
        </Field>
        <Field label="Customer email * (link goes here)">
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required className={inputClass} placeholder="owner@shop.com" />
        </Field>
      </div>

      {/* Pricing */}
      <SectionLabel className="mt-7">Pricing — one invoice, itemized</SectionLabel>
      <div className="mt-3 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={it.description}
              onChange={(e) => setItem(i, { description: e.target.value })}
              placeholder={i === 0 ? "eTX ImpaXt platform" : "e.g. Sunrise Ford — connected dealership"}
              className={cx(inputClass, "flex-1")}
            />
            <div className="relative w-36 shrink-0">
              <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-[14px] text-zinc-400">$</span>
              <input
                value={it.amount}
                onChange={(e) => setItem(i, { amount: e.target.value.replace(/[^\d.]/g, "") })}
                inputMode="decimal"
                placeholder="0"
                className={cx(inputClass, "pl-7 pr-12 text-right tabular-nums")}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-[12px] text-zinc-400">/mo</span>
            </div>
            <button
              type="button"
              onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
              disabled={items.length === 1}
              className="grid size-9 shrink-0 place-items-center rounded-lg text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 disabled:opacity-30"
              aria-label="Remove line item"
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={() => setItems((prev) => [...prev, { description: "", amount: "" }])}
            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
          >
            <Plus className="size-4" />
            Add line item
          </button>
          <p className="text-[14px] font-semibold tabular-nums text-zinc-900">
            Total {formatMoney(totalCents)}<span className="text-[12px] font-medium text-zinc-400">/mo</span>
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Contract term (months)">
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value.replace(/\D/g, "").slice(0, 2))}
            inputMode="numeric"
            className={inputClass}
            placeholder="3"
          />
        </Field>
      </div>

      {/* Internal routing — the customer fills their own shop details in the flow */}
      <SectionLabel className="mt-7">Internal</SectionLabel>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <Field label="Data-connection status (internal)">
          <select value={cciStatus} onChange={(e) => setCci(e.target.value as CciStatus)} className={inputClass}>
            {CCI_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* Rep */}
      <SectionLabel className="mt-7">You</SectionLabel>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <Field label="Your name *">
          <input value={salesRepName} onChange={(e) => setRepName(e.target.value)} required className={inputClass} placeholder="Jordan Ellis" />
        </Field>
        <Field label="Your email *">
          <input type="email" value={salesRepEmail} onChange={(e) => setRepEmail(e.target.value)} required className={inputClass} placeholder="you@etximpact.com" />
        </Field>
        <Field label="Your phone">
          <input value={salesRepPhone} onChange={(e) => setRepPhone(e.target.value)} className={inputClass} placeholder="(555) 010-4477" />
        </Field>
      </div>

      <div className="mt-7 flex justify-end">
        <button
          type="submit"
          disabled={submitting || totalCents <= 0}
          className="rounded-2xl bg-zinc-900 px-8 py-3 text-[14px] font-semibold text-white shadow-sm transition-colors hover:bg-zinc-800 disabled:bg-zinc-300"
        >
          {submitting ? "Generating…" : `Generate link — ${formatMoney(totalCents)}/mo`}
        </button>
      </div>
    </form>
  )
}

/* ── Table row ───────────────────────────────────────────── */

function SessionRow({
  s,
  onResend,
}: {
  s: OnboardingLinkSummary
  onResend: () => void
}) {
  const [resending, setResending] = useState(false)
  const resend = async () => {
    setResending(true)
    try {
      await onboardingLinksApi.resend(s.id)
      toast.success("Email resent")
      onResend()
    } catch {
      toast.error("Couldn’t resend")
    } finally {
      setResending(false)
    }
  }
  const resendable = s.status !== "call_booked" && s.status !== "expired"
  return (
    <tr className="hover:bg-zinc-50/60">
      <td className="px-5 py-3.5">
        <div className="font-semibold text-zinc-900">{s.shopName}</div>
        <div className="text-[11.5px] text-zinc-400">{s.contactEmail}</div>
      </td>
      <td className="px-5 py-3.5 text-zinc-600">
        {s.lineItems.length} item{s.lineItems.length > 1 ? "s" : ""} · {s.contractTermMonths}-mo term
      </td>
      <td className="px-5 py-3.5 font-medium tabular-nums text-zinc-900">
        {formatMoney(s.monthlyTotalCents)}
        <span className="text-[11px] text-zinc-400">/mo</span>
      </td>
      <td className="px-5 py-3.5">
        <StatusBadge status={s.status} />
      </td>
      <td className="px-5 py-3.5 text-right">
        {resendable && (
          <button
            onClick={resend}
            disabled={resending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-[12px] font-medium text-zinc-600 hover:bg-zinc-50"
          >
            <RefreshCw02 className={cx("size-3.5", resending && "animate-spin")} />
            Resend
          </button>
        )}
      </td>
    </tr>
  )
}

const STATUS_STYLE: Record<OnboardingStatus, { label: string; cls: string }> = {
  link_generated: { label: "Link generated", cls: "bg-zinc-100 text-zinc-600" },
  email_sent: { label: "Email sent", cls: "bg-blue-50 text-blue-700" },
  opened: { label: "Opened", cls: "bg-indigo-50 text-indigo-700" },
  details_completed: { label: "Details in", cls: "bg-fuchsia-50 text-fuchsia-700" },
  agreement_signed: { label: "Signed", cls: "bg-violet-50 text-violet-700" },
  call_booked: { label: "Call booked", cls: "bg-emerald-50 text-emerald-700" },
  expired: { label: "Expired", cls: "bg-zinc-100 text-zinc-400" },
}

function StatusBadge({ status }: { status: OnboardingStatus }) {
  const s = STATUS_STYLE[status]
  return (
    <span className={cx("inline-flex rounded-full px-2.5 py-1 text-[11.5px] font-semibold", s.cls)}>
      {s.label}
    </span>
  )
}

/* ── Form primitives (console-local) ─────────────────────── */

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cx("text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-400", className)}>
      {children}
    </p>
  )
}

const inputClass =
  "w-full rounded-xl border border-zinc-300 bg-white px-3.5 py-2.5 text-[14px] text-zinc-900 outline-none transition-colors placeholder:text-zinc-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12px] font-semibold text-zinc-700">{label}</span>
      {children}
    </label>
  )
}
